import React, { useEffect, useState } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { IoAddSharp } from 'react-icons/io5';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation, FaRegEye } from 'react-icons/fa6';
import { HiPencil } from 'react-icons/hi2';
import LeaseApiService from '../../services/apiService';
import { isAgentApiSuccess, unwrapAgentError, resolveBrokerId, pickAgentField, toAgentBool } from './agentMasterApiHelpers';
import AgentMasterForm from './agentMasterForm';
import DocumentExpiryModal from './documentExpiryModal';

const emptyValueFormatter = (params) => {
  const v = params.value;
  if (v === null || v === undefined || v === '' || v === '0') return '--';
  return v;
};

const AgentMasterListing = () => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [FilterTab, setFilterTab] = useState(true);
  const [nameFilter, setNameFilter] = useState('');
  const [formModal, setFormModal] = useState({ mode: null, data: null });
  const [showDocExpiry, setShowDocExpiry] = useState(false);

  useEffect(() => {
    getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // getlist has no pagination (confirmed live: returns the full matching array),
  // so search is a single "Name" filter matching the real request body, and
  // everything else is filtered client-side via the grid's own floating filters.
  const getItems = async (name = nameFilter) => {
    try {
      setLoading(true);
      const res = await apiService.post('AGENT_MASTER_LIST', { name: name || '' });
      if (isAgentApiSuccess(res?.data)) {
        setRowData(res?.data?.data || []);
      } else {
        setRowData([]);
        toast.warning(unwrapAgentError(res?.data));
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error(t('agentMaster.loadFailed'));
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setNameFilter('');
    getItems('');
  };

  const handleNew = () => setFormModal({ mode: 'create', data: null });

  const handleView = (row) => {
    if (!resolveBrokerId(row)) {
      toast.warning(t('agentMaster.missingRecordIdView'));
    }
    setFormModal({ mode: 'view', data: row });
  };

  const handleEdit = (row) => {
    if (!resolveBrokerId(row)) {
      toast.warning(t('agentMaster.missingRecordIdEdit'));
    }
    setFormModal({ mode: 'edit', data: row });
  };

  const closeFormModal = () => setFormModal({ mode: null, data: null });

  const handleFormSaved = () => {
    closeFormModal();
    getItems();
  };

  const columnDefs = [
    {
      headerName: t('common.view'),
      pinned: 'left',
      width: 70,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="center pt-1">
          <FaRegEye color="#6E5199" size={16} style={{ cursor: 'pointer' }} title={t('common.view')} onClick={() => handleView(params.data)} />
        </div>
      ),
    },
    {
      headerName: t('common.edit'),
      pinned: 'left',
      width: 70,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="center pt-1">
          <HiPencil color="#313a46" size={16} style={{ cursor: 'pointer' }} title={t('common.edit')} onClick={() => handleEdit(params.data)} />
        </div>
      ),
    },
    {
      headerName: t('agentMaster.columns.agentId'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      width: 130,
      valueGetter: (p) => resolveBrokerId(p.data),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.type'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      width: 120,
      valueGetter: (p) => pickAgentField(p.data, 'type', 'Type'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.name'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 200,
      valueGetter: (p) => pickAgentField(p.data, 'broker_Name', 'name'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.contactPerson'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 160,
      valueGetter: (p) => pickAgentField(p.data, 'contactPerson'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.phoneNo'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      width: 140,
      valueGetter: (p) => pickAgentField(p.data, 'brokerLandLineNo', 'landlineNo'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.mobileNo'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      width: 140,
      valueGetter: (p) => pickAgentField(p.data, 'brokerMobileNo', 'mobileNo'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.emailId'),
      suppressMovable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      flex: 1,
      minWidth: 200,
      valueGetter: (p) => pickAgentField(p.data, 'brokerEmailID', 'email'),
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('agentMaster.columns.active'),
      suppressMovable: true,
      sortable: true,
      width: 100,
      valueGetter: (p) => (toAgentBool(p.data?.active) ? t('common.yes') : t('common.no')),
    },
  ];

  return (
    <div className="p-3">
      <div className="cost-center-section">
        <div className="d-flex mt-3 cursor-pointer slide-header" onClick={() => setFilterTab(!FilterTab)}>
          <div className="h6 pt-1">{t('common.searchParameters')}</div>
        </div>
        <div className={`slide-content ${FilterTab ? 'open' : 'closed'}`}>
          <div className={`border-box2 p-3 ${FilterTab ? 'slide-in' : 'slide-out'}`}>
            <div className="d-flex gap-3 align-items-stretch">
              <div className="form-group grid-contain flex-grow-1">
                <div className="d-flex">
                  <label className="form-label pt-2 start">{t('agentMaster.filters.name')}</label>
                  <input
                    type="text"
                    className="p-2 form-control w-75"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-1 align-items-start">
                <div onClick={() => getItems()} className="mainbtn flex-end gap-2">
                  <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                  <div className="center">{t('common.search')}</div>
                </div>
                <div onClick={handleClearFilters} className="mainbtnRed flex-end gap-2">
                  <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                  <div className="center">{t('common.reset')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-box2 p-3">
        <a href="#!" className="d-inline-block mb-2" onClick={(e) => { e.preventDefault(); setShowDocExpiry(true); }}>
          {t('agentMaster.documentExpiredList')}
        </a>
        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <>
            <div className="d-flex justify-contant-between ">
              <h5 className="col mainInvHead">{t('agentMaster.pageTitle')}</h5>
              <div className="d-flex justify-content-end gap-1">
                <div className="col center">
                  <button className="mainbtn" type="button" onClick={handleNew}>
                    <IoAddSharp color="#fff" className="center mt-1" /> <div>{t('common.add')}</div>
                  </button>
                </div>
              </div>
            </div>
            <div className="gridshad mt-3">
              <div className="ag-theme-quartz" style={{ height: 650 }}>
                <AgGridReact
                  getRowStyle={(params) => ({
                    background: params.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff',
                  })}
                  rowData={rowData}
                  columnDefs={columnDefs}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <Modal
        show={!!formModal.mode}
        onHide={closeFormModal}
        backdrop="static"
        keyboard={false}
        centered
        dialogClassName="customer-form-modal"
        contentClassName="p-0"
      >
        <Modal.Body style={{ padding: 0 }}>
          {formModal.mode && <AgentMasterForm mode={formModal.mode} initialData={formModal.data} onClose={closeFormModal} onSaved={handleFormSaved} />}
        </Modal.Body>
      </Modal>

      <DocumentExpiryModal show={showDocExpiry} onClose={() => setShowDocExpiry(false)} />
    </div>
  );
};

export default AgentMasterListing;
